<template>
  <div class="evaluation-type-select-wrap">
    <div
      class="select-trigger"
      :class="{ 'is-open': dropdownVisible, 'is-disabled': disabled }"
      @click="handleTriggerClick"
    >
      <span
        class="selected-summary"
        :class="{ placeholder: !selectedLabelText }"
        :title="selectedLabelText || '请选择评价类型'"
      >
        {{ selectedLabelText || '请选择评价类型' }}
      </span>
      <i class="el-icon-arrow-down arrow-icon" :class="{ 'is-open': dropdownVisible }"></i>
    </div>

    <div v-show="dropdownVisible" class="dropdown-panel">
      <div class="dropdown-scroll">
        <el-tree
          v-if="treeOptions.length > 0"
          ref="tree"
          :data="treeOptions"
          node-key="id"
          :props="defaultProps"
          :expand-on-click-node="false"
        >
          <span class="tree-node" slot-scope="{ data }">
            <!-- is_department=0 的节点是可选中的评价类型，支持多选 -->
            <el-checkbox
              v-if="isSelectableNode(data)"
              :checked="!!data.checked"
              @change="handleNodeChange(data)"
            >
              {{ data.show_name }}
            </el-checkbox>
            <!-- 其余节点当成目录，只用于展开浏览 -->
            <span v-else class="tree-parent-label">{{ data.show_name }}</span>
          </span>
        </el-tree>
        <div v-else class="empty-text">暂无数据</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EvaluationTypeSelect',
  props: {
    // 评价类型树（父组件已请求 /api/Management/pte_list）
    options: {
      type: Array,
      default: () => [],
    },
    // 已选中的 id 数组（英文逗号字符串拆分而来）
    selectedId: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      treeOptions: [],
      optionLabelMap: {},
      selectedIdsInternal: [],
      dropdownVisible: false,
      defaultProps: {
        children: 'children',
        label: 'show_name',
      },
    }
  },
  computed: {
    selectedLabelText() {
      return this.selectedIdsInternal
        .map((id) => this.optionLabelMap[String(id)] || '')
        .filter((label) => label)
        .join('、')
    },
  },
  watch: {
    options: {
      handler(val) {
        const { treeOptions, labelMap } = this.buildTreeOptions(val)
        this.treeOptions = treeOptions
        this.optionLabelMap = labelMap
        this.syncCheckedState()
      },
      deep: true,
      immediate: true,
    },
    selectedId: {
      handler(val) {
        this.selectedIdsInternal = this.normalizeIds(val)
        this.syncCheckedState()
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    normalizeIds(val) {
      if (!Array.isArray(val)) return []
      return val.map((item) => String(item)).filter((item) => item)
    },
    // 只有 is_department=0 的节点可勾选；其余（含 is_department=1）当成目录
    isSelectableNode(node) {
      return String(node.is_department) === '0'
    },
    walkNodes(nodes, callback) {
      ;(nodes || []).forEach((node) => {
        callback(node)
        if (node.children && node.children.length > 0) {
          this.walkNodes(node.children, callback)
        }
      })
    },
    buildTreeOptions(list) {
      const labelMap = {}
      let hasDepartmentFlag = false
      const cloneNodes = (nodes) => {
        return (nodes || []).map((item) => {
          const children = cloneNodes(item.children || [])
          if (item.is_department !== undefined && item.is_department !== null) {
            hasDepartmentFlag = true
          }
          const nextNode = {
            id: String(item.pte_id || item.id || item.value || item.key || ''),
            show_name: item.pte_name || item.show_name || item.name || item.label || '',
            is_department:
              item.is_department !== undefined ? String(item.is_department) : '',
            checked: false,
            children,
          }
          if (nextNode.id && nextNode.show_name) {
            labelMap[nextNode.id] = nextNode.show_name
          }
          return nextNode
        })
      }
      const treeOptions = cloneNodes(list).filter((item) => item.id && item.show_name)
      if (treeOptions.length > 0 && !hasDepartmentFlag) {
        console.warn(
          '[EvaluationTypeSelect] 接口未返回 is_department 字段，按规则所有节点都会当成目录（不可勾选），请检查接口返回'
        )
      }
      return { treeOptions, labelMap }
    },
    syncCheckedState() {
      const selectedIdSet = new Set(this.selectedIdsInternal.map((id) => String(id)))
      this.walkNodes(this.treeOptions, (node) => {
        node.checked =
          this.isSelectableNode(node) && selectedIdSet.has(String(node.id))
      })
    },
    handleNodeChange(node) {
      if (this.disabled || !this.isSelectableNode(node)) return
      const nodeId = String(node.id)
      const nextSet = new Set(this.selectedIdsInternal)
      if (nextSet.has(nodeId)) {
        nextSet.delete(nodeId)
      } else {
        nextSet.add(nodeId)
      }
      this.selectedIdsInternal = Array.from(nextSet)
      this.syncCheckedState()
      this.$emit('change', this.selectedIdsInternal)
    },
    handleTriggerClick() {
      if (this.disabled) return
      this.dropdownVisible = !this.dropdownVisible
    },
    handleDocumentClick(event) {
      if (!this.$el.contains(event.target)) {
        this.dropdownVisible = false
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleDocumentClick)
  },
}
</script>

<style lang="less" scoped>
.evaluation-type-select-wrap {
  position: relative;
  width: 100%;
}

.select-trigger {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 40px;
  padding: 0 36px 0 14px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  box-sizing: border-box;
  cursor: pointer;
  transition: border-color 0.2s;
}

.select-trigger.is-open,
.select-trigger:hover {
  border-color: #409eff;
}

.select-trigger.is-disabled {
  background: #f5f7fa;
  border-color: #e4e7ed;
  cursor: not-allowed;
}

.selected-summary {
  display: block;
  width: 100%;
  line-height: 22px;
  color: #606266;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selected-summary.placeholder {
  color: #c0c4cc;
}

.arrow-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  margin-top: -7px;
  color: #c0c4cc;
  transition: transform 0.2s;
}

.arrow-icon.is-open {
  transform: rotate(180deg);
}

.dropdown-panel {
  position: absolute;
  left: 0;
  right: 0;
  top: 44px;
  z-index: 20;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.dropdown-scroll {
  max-height: 260px;
  overflow-y: auto;
  padding: 8px 0;
}

.tree-node {
  display: inline-flex;
  align-items: center;
  width: 100%;
  padding-right: 12px;
  box-sizing: border-box;
}

.tree-parent-label {
  display: inline-block;
  color: #303133;
  font-weight: 600;
}

.empty-text {
  padding: 12px 16px;
  color: #909399;
  font-size: 14px;
}

/deep/ .el-tree-node__content {
  min-height: 34px;
}

/deep/ .el-checkbox__label {
  color: #606266;
}
</style>
